/**
 * PersonAvatar / PersonAvatarStack
 *
 * A project spans organizations, so "who is this?" has to be answerable at a
 * glance in three different places: the members board, a conversation row's
 * contributor strip, and beside each message. All three use this component so a
 * person looks IDENTICAL everywhere — same initials, same colour, same tooltip.
 *
 * The colour is hashed from the user id (falling back to the label) onto the
 * `--hierarchy-0…4` semantic tokens. Hashing the id rather than the name means
 * two people called "David" stay tellable apart, and using tokens rather than
 * hard-coded hues means the palette follows the tenant's brand and the theme.
 */

import { useLanguage } from '@/hooks/useLanguage';
import { getInitials } from '@/utils/getInitials';
import './PersonAvatar.css';

/** Number of hierarchy tint tokens available (--hierarchy-0 … --hierarchy-4). */
const PALETTE_SIZE = 5;

/** Stable, order-independent hash. Same input → same swatch, forever. */
function paletteIndex(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0; // force 32-bit
  }
  return Math.abs(hash) % PALETTE_SIZE;
}

export interface PersonAvatarProps {
  /** Stable identity for the colour hash. Prefer the user id. */
  seed?: string | null;
  /** Display name; falls back to the email's local part. */
  name?: string | null;
  email?: string | null;
  /** Organization label, shown under the name in the tooltip. */
  tenantName?: string | null;
  size?: number;
  /** Renders a check affordance for "this person has written here". */
  contributed?: boolean;
  className?: string;
}

export function PersonAvatar({
  seed,
  name,
  email,
  tenantName,
  size = 28,
  contributed = false,
  className = '',
}: PersonAvatarProps) {
  const { t } = useLanguage();
  const label = (name || email || '').trim();
  const swatch = paletteIndex(String(seed || label || '?'));

  // Native `title` rather than a custom tooltip: it is keyboard- and
  // screen-reader-reachable for free, and this is a label, not a menu.
  const tooltip = [
    label || t('projects.unknownPerson') || 'Unknown',
    tenantName || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <span
      className={`person-avatar ${className}`}
      data-swatch={swatch}
      data-contributed={contributed ? 'true' : undefined}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36) }}
      title={tooltip}
      aria-label={tooltip}
      role="img"
    >
      {getInitials(label || '?')}
    </span>
  );
}

export interface PersonAvatarStackProps {
  people: Array<{
    id?: string | null;
    name?: string | null;
    email?: string | null;
    tenantName?: string | null;
  }>;
  /** Faces shown before collapsing into "+N". */
  max?: number;
  size?: number;
  className?: string;
}

/**
 * Overlapping face pile with a `+N` overflow chip. The chip carries the names
 * of everyone it hides in its tooltip — a bare "+4" that can't be interrogated
 * is a dead end in a workspace whose whole point is knowing who is present.
 */
export function PersonAvatarStack({
  people,
  max = 4,
  size = 26,
  className = '',
}: PersonAvatarStackProps) {
  const { t } = useLanguage();
  if (!people.length) return null;

  const shown = people.slice(0, max);
  const hidden = people.slice(max);
  const hiddenLabel = hidden
    .map(p => (p.name || p.email || '').trim())
    .filter(Boolean)
    .join(', ');

  return (
    <span className={`person-stack ${className}`} style={{ ['--stack-size' as string]: `${size}px` }}>
      {shown.map((person, index) => (
        <PersonAvatar
          key={person.id || person.email || index}
          seed={person.id}
          name={person.name}
          email={person.email}
          tenantName={person.tenantName}
          size={size}
        />
      ))}
      {hidden.length > 0 && (
        <span
          className="person-stack-more"
          style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
          title={hiddenLabel || (t('projects.moreContributors') || 'More contributors')}
          aria-label={hiddenLabel}
        >
          +{hidden.length}
        </span>
      )}
    </span>
  );
}

export default PersonAvatar;
